/* eslint-disable no-undef, new-cap, react/sort-comp, no-unused-vars,
class-methods-use-this, no-param-reassign */
import React from "react"
import Immutable from "immutable"
import PropTypes from "prop-types"
import constants from "imports/desktopConstants"
import Dynamo from "graphs/components/Dynamo"
import { getElementContentWidth } from "imports/desktopHelperFunctions"
import { ButtonToolbar, Button } from 'react-bootstrap'

/*
    Network Graph Wrapper Component
    -- this part handles the appearance of nodes and links as well as mouseevents
    Dynamo actually creates and maintains the d3 viz (the position of
    nodes and links at any given time)
*/

// node fill and stroke colors
const DEFAULT_COLOR = constants.QUORUM_BLUE
const DEFAULT_STROKE_COLOR = "white"
const SELECTED_NODE_STROKE_COLOR = "#000000"
const HOVERED_NODE_STROKE_COLOR = "#070707"
const HOVERED_NODE_TARGET_STROKE_COLOR = "#393c42"
const DEFAULT_STROKE_WIDTH = "2px"
const DEFAULT_FONT_SIZE = "12px"

// link stroke color
const LINK_COLOR = "#ccc"
const SELECTED_LINK_COLOR = "#555"
const SELECTED_LINK_OPACITY = 1

const MIN_LINE_WEIGHT = 1
const MAX_LINE_WEIGHT = 10
const MIN_UNHOVERED_LINE_OPACITY = 0.35
const MAX_UNHOVERED_LINE_OPACITY = 0.5
const MIN_NODE_RADIUS = 5
const MAX_NODE_RADIUS = 15
const CENTER_NODE_RADIUS = MAX_NODE_RADIUS + 5
// ratio of desired circles to white space, used to determine default circle radius
const NODE_WHITE_SPACE_RATIO = 1 / 5
const DEFAULT_GRAVITY = 0.15

const moveToFront = (svgElement, useParentNode) => {
    let gElement = svgElement[0][0]
    if (useParentNode) {
        gElement = gElement.parentNode
    }
    const container = gElement.parentNode
    container.appendChild(gElement)
}

const moveToBack = (svgElement, useParentNode) => {
    let gElement = svgElement[0][0]
    if (useParentNode) {
        gElement = gElement.parentNode
    }
    const container = gElement.parentNode
    const firstChild = container.firstChild
    if (firstChild) {
        container.insertBefore(gElement, firstChild)
    }
}

export default class NetworkGraph extends React.Component {
    componentDidMount() {
        const graphProps = this.getGraphProps()

        this.graph = new Dynamo(graphProps)
        this.graph.updateData(this.props.data)

        // add a tooltip
        this.tooltip = Tooltip(`${this.props.id}-tooltip`)
    }

    componentDidUpdate(prevProps) {
        if (!Immutable.fromJS(prevProps.data.nodes).equals(Immutable.fromJS(this.props.data.nodes)) ||
            !Immutable.fromJS(prevProps.data.links).equals(Immutable.fromJS(this.props.data.links))
        ) {
            this.graph.updateData(this.props.data)
        }
        this.graph.updateProps(this.getGraphProps())
    }

    componentWillUnmount() {
        // remove tooltip
        d3.select(`#${this.props.id}-tooltip`).remove()
    }

    getDimensions() {
        return {
            width: getElementContentWidth(this.props.wrapperId),
            height: this.props.desiredHeight || 450,
        }
    }

    getGraphProps() {
        const { width, height } = this.getDimensions()
        return {
            width,
            height,
            graphId: `${this.props.id}`,
            getNodeEnterAttributes: this.getNodeEnterAttributes.bind(this),
            getLinkEnterAttributes: this.getLinkEnterAttributes.bind(this),
            getNodeTransitionAttributes: this.getNodeTransitionAttributes.bind(this),
            getLinkTransitionAttributes: this.getLinkTransitionAttributes.bind(this),
            handleNodeOnClick: this.handleNodeOnClick.bind(this),
            handleNodeOnMouseover: this.handleNodeOnMouseover.bind(this),
            handleNodeOnMouseout: this.handleNodeOnMouseout.bind(this),
            isLabelVisible: this.isLabelVisible.bind(this),
            getNodeLabel: this.props.getNodeLabel,
            data: this.props.data,
            scales: this.getScales(),
            allowZoom: this.props.allowZoom,
            centerNodeId: this.props.centerNodeId,
            gravity: this.props.gravity,
        }
    }

    getScales() {

        const { width, height } = this.getDimensions()

        // node colorScale
        const minColorValue = d3.min(this.props.data.nodes, node => node.color)
        let maxColorValue = d3.max(this.props.data.nodes, node => node.color)
        if (minColorValue === maxColorValue) {
            maxColorValue += 1
        }

        let colorScale = () => {}
        if (this.props.nodeColors) {
            colorScale = nodeId => this.props.nodeColors(nodeId)
        } else if (this.props.data.nodes[0] &&
            Object.keys(this.props.data.nodes[0]).includes("color")
        ) {
            // check if color property given for node
            colorScale = d3.scale.quantize()
                .domain([minColorValue, maxColorValue])
                .range(colorbrewer.Set1[9])
        } else {
            colorScale = () => DEFAULT_COLOR
        }

        // node size
        const nodeValues = Object.values(this.props.nodeMap)
        const getSizeForNode = node => node[this.props.sizeKey]
        const minSizeValue = d3.min(nodeValues, getSizeForNode)
        const maxSizeValue = d3.max(nodeValues, getSizeForNode)

        const radiusScale = d3.scale.linear()
            .domain([minSizeValue, maxSizeValue])
            .range([MIN_NODE_RADIUS, MAX_NODE_RADIUS])

        // line weights
        const minWeight = d3.min(this.props.data.links, link => link[this.props.lineWeightKey])
        let maxWeight = d3.max(this.props.data.links, link => link[this.props.lineWeightKey])
        if (minWeight === maxWeight) {
            maxWeight += 1
        }

        const lineOpacityScale = d3.scale.linear()
            .domain([minWeight, maxWeight])
            .range([MIN_UNHOVERED_LINE_OPACITY, MAX_UNHOVERED_LINE_OPACITY])

        const lineWeightScale = d3.scale.linear()
            .domain([minWeight, maxWeight])
            .range([MIN_LINE_WEIGHT, MAX_LINE_WEIGHT])

        // line strengths (charge)
        const minStrength = d3.min(this.props.data.links, link => link[this.props.lineStrengthKey])
        let maxStrength = d3.max(this.props.data.links, link => link[this.props.lineStrengthKey])
        if (minWeight === maxWeight) {
            maxWeight += 1
        }

        const linkStrengthScale = d3.scale.sqrt()
            .domain([minStrength, maxStrength])
            .range([.7, 1])

        const linkDistanceScale = d3.scale.sqrt()
            .domain([minStrength, maxStrength])
            .range([140, 70])

        return {
            color: colorScale,
            radius: radiusScale,
            lineWeight: lineWeightScale,
            lineOpacity: lineOpacityScale,
            linkStrength: linkStrengthScale,
            linkDistance: linkDistanceScale,
        }
    }

    getNodeEnterAttributes() {
        const scales = this.getScales()
        return ({
            fill: node => scales.color(node.id),
            r: node => (node.id === this.props.centerNodeId
                ? CENTER_NODE_RADIUS
                : scales.radius(this.getNodeDataObject(node)[this.props.sizeKey])),
            stroke: () => "white",
            "stroke-width": DEFAULT_STROKE_WIDTH
        })
    }

    getLinkEnterAttributes() {
        const scales = this.getScales()
        return ({
            stroke: link => "black",
            "stroke-width": link => scales.lineWeight(link.value) + 'px'
        })
    }

    /*
        On update, color nodes in this.props.selectedNodes
        with dark stroke
     */
    getNodeTransitionAttributes() {
        const scales = this.getScales()
        return ({
            fill: node => scales.color(node.id),
            stroke: node => (this.isNodeSelected(node)
                ? SELECTED_NODE_STROKE_COLOR
                : DEFAULT_STROKE_COLOR
            ),
            r: node => (node.id === this.props.centerNodeId
                ? CENTER_NODE_RADIUS
                : scales.radius(this.getNodeDataObject(node)[this.props.sizeKey])),
        })
    }

    /*
        On update, color links in this.props.selectedLinks
        with dark stroke.
     */
    getLinkTransitionAttributes() {
        const scales = this.getScales()
        return ({
            stroke: LINK_COLOR,
            opacity: link => scales.lineOpacity(link.value),
            "stroke-width": link => scales.lineWeight(link.value)
        })
    }

    /*
        Click handlers change filters, so parent takes care of this.
        This triggers a setState, so getNodeTransitionAttributes()
        and getLinkTransitionAttributes() will be called after this
        runs.
     */
    handleNodeOnClick(node, d3Event, svgNode) {
        moveToFront(svgNode, true)
        this.props.onNodeClick(node)
    }

    /*
        Mouseover and mouseout handlers
        Only case we need to check for is whether or not the parent
        has given the functions getNodeTooltipContent and getLinkTooltipContent.
        If we don't have those, then we won't display the tooltip.
     */

    handleNodeOnMouseover(node, d3Event, svgNode, svgNodeNeighbors, svgConnectingLinks) {
        // show tooltip
        if (this.props.getNodeTooltipContent) {
            const content = this.props.getNodeTooltipContent(node.id)
            this.tooltip.showTooltip(content, d3Event)
        }

        /* Style nodes and links */
        const scales = this.getScales()
        // if this is the centerNode, don't highlight the links and neighbros since that would highlight everything
        // any thus wouldn't be very helpful
        if (node.id !== this.props.centerNodeId) {
            // darken nodes connected links' strokes
            svgConnectingLinks.forEach(svgLink => {
                // link is sometimes beneath other links, so bring it to the front
                // svgLink.moveToFront()
                svgLink.attr({
                    stroke: SELECTED_LINK_COLOR,
                    opacity: SELECTED_LINK_OPACITY
                })

                moveToFront(svgLink)
            })

            // darken nodes neighbors' strokes
            svgNodeNeighbors.forEach(svgNeighbor => {
                svgNeighbor.attr({
                    stroke: HOVERED_NODE_STROKE_COLOR,
                })

                moveToFront(svgNeighbor, true)
            })
        }

        // darken node's fill and stroke
        svgNode.attr({
            stroke: HOVERED_NODE_STROKE_COLOR
        })

        moveToFront(svgNode, true)
    }

    /*
        When leaving a node, there are 2 case:
            1. node is a selectedNode (was clicked after mouseenter or had already been clicked in past)
            2. node is not selected

        For node fill color,
        The node color is always reset on mouseout

        For node stroke color,
        If node is a selectedNode, then it's stroke will be the SELECTED_NODE_STROKE_COLOR
        Otherwise, stroke will be the DEFAULT_STROKE_COLOR

        For link stroke opacity,
        If link is a selectedLink, then it's opacity is SELECTED_LINK_OPACITY
        Otherwise, use DEFAULT_LINK_COLOR
     */
    handleNodeOnMouseout(node, d3Event, svgNode, svgNodeNeighbors, svgConnectingLinks) {
        const scales = this.getScales()
        this.tooltip.hideTooltip()
        // reset node's stroke and fill
        svgNode.attr({
            fill: scales.color(node.id),
            stroke: (this.isNodeSelected(node)
                ? SELECTED_NODE_STROKE_COLOR
                : DEFAULT_STROKE_COLOR
            )
        })
        // reset neighbors and links
        svgNodeNeighbors.forEach(svgNode => {
            const nodeData = this.getDataFromSVGElement(svgNode)
            svgNode.attr({
                stroke: (this.isNodeSelected(nodeData)
                    ? SELECTED_NODE_STROKE_COLOR
                    : DEFAULT_STROKE_COLOR
                )
            })
        })
        // reset connected links' strokes
        svgConnectingLinks.forEach(svgLink => {
            // get link data from svg element
            const link = this.getDataFromSVGElement(svgLink)
            svgLink.attr({
                stroke: LINK_COLOR,
                opacity: (this.isLinkSelected(link)
                    ? SELECTED_LINK_OPACITY
                    : scales.lineOpacity(link.value))

            })
            // when hovering over a link, we used moveToFront() to make sure the link was above
            // all other links. To make sure we don't lay an unselected link on top of selected link
            // onMouseout, we need to send the links to the back if they are not selected
            if (!this.isLinkSelected(link)) {
                moveToBack(svgLink)
            }
        })
    }

    // the helper squad

    isLabelVisible(node) {
        // show label if parent says to show labels and parent included it in list
        // of important nodes that should have labels
        // or if it's been selected in the graph

        // get all nodes selected by clicking links (saved as `${sourceId}-to-${targetId}`)
        const nodesSelectedThroughLinks = this.props.selectedLinks
            .map(i => i.split("-to-"))
            .reduce((total, d) => {return total.concat(d)}, [])
        return (
            (this.props.showLabels && this.props.nodesWithLabels.includes(node.id)) ||
            (this.props.selectedNodes.includes(node.id) || nodesSelectedThroughLinks.includes(node.id))
        )
    }

    getDataFromSVGElement(svgElement) {
        return svgElement[0][0].__data__
    }

    getNodeDataObject(node) {
        return this.props.nodeMap[node.id]
    }

    isNodeSelected(node) {
        return this.props.selectedNodes.includes(node.id)
    }

    isLinkSelected(link) {
        return this.props.selectedLinks.includes(`${link.source.id}-to-${link.target.id}`)
    }

    render() {
        return (
            <div className="graph-wrapper">
                <div id={this.props.id} />
                {
                    this.props.allowZoom &&
                    <div className="zoom-button-wrapper">
                        <ButtonToolbar>
                            <button
                                className={'btn btn-default-white'}
                                onClick={() => this.graph.zoomIn()}
                            >
                                {"Zoom In"}
                            </button>
                            <button
                                className={'btn btn-default-white'}
                                onClick={() => this.graph.zoomOut()}
                            >
                                {"Zoom Out"}
                            </button>
                            <button
                                className={'btn btn-default-white'}
                                onClick={() => this.graph.zoomReset()}
                            >
                                {"Reset Zoom"}
                            </button>
                        </ButtonToolbar>
                    </div>
                }
            </div>
        )
    }
}

NetworkGraph.propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.shape({
        nodes: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            color: PropTypes.number,
            size: PropTypes.number,
            label: PropTypes.string
        })).isRequired,
        links: PropTypes.arrayOf(PropTypes.shape({
            source: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            target: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            frequency: PropTypes.number.isRequired
        })).isRequired
    }).isRequired,
    // takes in node.color and returns a color
    nodeColors: PropTypes.func,
    getNodeTooltipContent: PropTypes.func,
    getLinkTooltipContent: PropTypes.func,
    onNodeClick: PropTypes.func,
    onLinkClick: PropTypes.func,
    // selectedNodes and selectedLinks will be SELECTEDed with dark stroke
    selectedNodes: PropTypes.array,
    // selectedLinks given as an array of strings = [`${sourceName}-to-${targetName}`, ...]
    selectedLinks: PropTypes.array,
    showLabels: PropTypes.bool,
    nodesWithLabels: PropTypes.array,
}

NetworkGraph.defaultProps = {
    // args: (node.name)
    getNodeTooltipContent: null,
    // args: (link)
    getLinkTooltipContent: null,
    onNodeClick: (id) => {},
    onLinkClick: (id) => {},
    nodeColors: null,
    selectedNodes: [],
    selectedLinks: [],
    showLabels: true,
    nodesWithLabels: null,
    allowZoom: true,
    centerNodeId: null,
    gravity: DEFAULT_GRAVITY,
    disableLinkHover: false
}
